// MACHINERY V2 (Stroje) — localStorage hydration for the UI slice (split out of
// machineryV2UiSlice.ts at M2.13).
//
// WHY THIS FILE EXISTS: M2.13's persisted fleet view (ruling 3) pushed the slice
// to within 3 lines of the project's 300-line cap, leaving M2.14's `"V4"` member
// no room to land. The arithmetic, re-measured at commit time rather than
// carried over from the previous pass:
//   · pre-M2.13 slice ....... 258  (`git show HEAD:<slice> | wc -l`)
//   · un-split M2.13 slice ... 297  (reconstructed literally, not estimated: the
//     post-split slice, minus its 11-line import block and 4-line "moved to"
//     note, with all 87 lines of helper content below inlined back in)
//   · post-split slice ....... 225
// M2.13 therefore added a net 39 lines to the slice. An earlier draft of this
// header claimed 298 — off by one; a review counter-proposal of ~282 was low in
// the other direction, having omitted the ~25 lines the new readFleetView /
// writeFleetView pair would need inline. The split stands on the corrected
// number: 297 with `"V4"` still pending is out of room either way.
//
// The seam is the same one cardStateStyles.ts used for cardStates.ts: pure
// helpers that decide NOTHING about state. Everything below moved from the
// slice's "localStorage hydration" block. Two deltas, not a byte-for-byte move:
// the `readFleetView` / `writeFleetView` pair is new at M2.13, written to the
// same shape as `readPalette` / `writePalette`; and the readers were rewired
// onto `safeGetItem` in the M2.13 fix pass (see its note below).
//
// EVERY WRITE IS NON-FATAL. localStorage throws in private mode and on quota,
// and a failed UI PREFERENCE must never break the page — so each writer swallows
// its own error, exactly as `writePalette` has since M2.0. Reads are validated
// against the closed value lists below: a stored value from a future (or
// removed) member must not put the fleet into a state no branch renders.
//
// The type import is `import type` ON PURPOSE: the slice imports this module for
// values, so a value import back would be a runtime cycle. Erasing it at compile
// time is the same trick the slice itself uses for MachineryV2CardState.
//
// No MUI, no @emotion, no `sx`, no `dt` tokens.
import type {
  MachineryV2FleetView,
  MachineryV2GroupBy,
  MachineryV2Palette,
} from "./machineryV2UiSlice";

const LS_PALETTE_KEY = "machinery-v2-palette";
/** Read-only seed: the LIVE grid owns this key. v2 never writes it, so
 *  toggling the V2 grouping cannot change what /machinery does. */
const LS_LEGACY_GROUP_BY_KEY = "machinery_groupBy";
const LS_RAIL_COLLAPSED_KEY = "machinery-v2-rail-collapsed";
/** Its own key — nothing else reads or writes it, in v2 or on the live grid. */
const LS_FLEET_VIEW_KEY = "machinery-v2-fleet-view";

const safeLocalStorage = (): Storage | null => {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    return null;
  }
};

/**
 * EVERY READ GOES THROUGH HERE. `safeLocalStorage()` only guards OBTAINING the
 * Storage object; `getItem` can itself throw (Safari private mode, a hardened
 * or SecurityError-throwing profile) and every reader below called it bare.
 * These readers run at MODULE LOAD, inside the slice's `initialState`, so a
 * throw takes out the IMPORT — a blank app, not one stale preference.
 *
 * Each caller keeps its own closed-list validation and its own default; this
 * only turns "threw" into "absent", which every reader already handles. Zero
 * happy-path behaviour change.
 */
const safeGetItem = (key: string): string | null => {
  const ls = safeLocalStorage();
  if (!ls) return null;
  try {
    return ls.getItem(key);
  } catch {
    return null;
  }
};

export const readPalette = (): MachineryV2Palette => {
  const raw = safeGetItem(LS_PALETTE_KEY);
  if (raw === "claude" || raw === "machineid") return raw;
  return "claude";
};

export const writePalette = (palette: MachineryV2Palette): void => {
  const ls = safeLocalStorage();
  if (!ls) return;
  try {
    ls.setItem(LS_PALETTE_KEY, palette);
  } catch {
    // localStorage may throw in private mode / on quota; not fatal for v2 UX.
  }
};

const FLEET_VIEW_VALUES: MachineryV2FleetView[] = ["V1", "V2", "V3", "V4"];

/**
 * The closed-list check on its own, so the `?view=` URL escape (M2.14 — the
 * kiosk's recoverable way out, FleetPage) validates against the SAME list a
 * stored value does. Two validators for one union is how a member added to one
 * and not the other becomes a view that loads from localStorage but not from a
 * link. Returns null for "not a view", which is a different answer from
 * `readFleetView`'s "fall back to V1": an absent param must change nothing.
 */
export const parseFleetView = (raw: string | null): MachineryV2FleetView | null =>
  FLEET_VIEW_VALUES.indexOf(raw as MachineryV2FleetView) >= 0
    ? (raw as MachineryV2FleetView)
    : null;

/** Validated on read: a stored value from a future (or removed) view must not
 *  put the fleet into a state no branch renders. Unknown -> the V1 default. */
export const readFleetView = (): MachineryV2FleetView =>
  parseFleetView(safeGetItem(LS_FLEET_VIEW_KEY)) || "V1";

export const writeFleetView = (view: MachineryV2FleetView): void => {
  const ls = safeLocalStorage();
  if (!ls) return;
  try {
    ls.setItem(LS_FLEET_VIEW_KEY, view);
  } catch {
    // Same rationale as writePalette: private mode / quota can throw, and a
    // failed preference write must never break the page.
  }
};

const GROUP_BY_VALUES: MachineryV2GroupBy[] = [
  "type", "location", "line", "department", "all",
];

export const readGroupBy = (): MachineryV2GroupBy => {
  const raw = safeGetItem(LS_LEGACY_GROUP_BY_KEY);
  return GROUP_BY_VALUES.indexOf(raw as MachineryV2GroupBy) >= 0
    ? (raw as MachineryV2GroupBy)
    : "type";
};

export const readRailCollapsed = (): boolean =>
  safeGetItem(LS_RAIL_COLLAPSED_KEY) === "true";

export const writeRailCollapsed = (collapsed: boolean): void => {
  const ls = safeLocalStorage();
  if (!ls) return;
  try {
    ls.setItem(LS_RAIL_COLLAPSED_KEY, String(collapsed));
  } catch {
    // Same rationale as writePalette.
  }
};
